import { PayloadAction } from '@reduxjs/toolkit';
import { EditableField } from '@/types/editableField';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { EdsEntityUniqueFields } from '@/domains/milestones/types/EdsEntityUniqueFields';
import {
  GetAvailableMilestonesPayload,
  GetMilestoneRelatedEntityListPayload,
  MilestoneValidationResult,
  NotValidItem,
  SetNextMilestonesPayload,
  ValidateMilestonePayload,
} from '@/domains/milestones/types/RequestedItemPayloads';
import { EntityField } from '@/features/editMode/types/EntityField';
import React from 'react';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import EditModeEvents from '@/features/editMode/events';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { getClaimSetV2, selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { getClaimantsList } from '@/domains/claimants/store/claimantsListSlice';
import { getClaimSetClaimListData } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import { RequestedDataEntityLevel } from '@/domains/milestones/enums/RequestedItemEntityLevel';
import { RequestedItemSlots } from '@/domains/milestones/enums/RequestedItemSlots';
import {
  selectIsAllAvailableMilestonesLoading,
  selectNotValidRequestedItems,
  selectNotValidTasks,
} from '@/domains/milestones/store/milestonesSelectors';
import {
  getAllAvailableMilestones,
  getAllAvailableMilestonesSuccess,
  getEntityMilestoneDetails,
  getEntityMilestoneDetailsError,
  getEntityMilestoneDetailsSuccess,
  getMilestoneRelatedEntityList,
  getMilestoneRelatedEntityListSuccess,
  getMilestoneWithCategoryOptions,
  getMilestoneWithCategoryOptionsSuccess,
  getNotValidRequestedItems,
  getNotValidRequestedItemsSuccess,
  getNotValidTasks,
  getNotValidTasksSuccess,
  saveNewMilestoneState,
  setGetMilestoneOptionsLoading,
  setIsAvaliableMilestonesLoading,
  setIsNextMilestonePossible,
  setIsNotValidRequestedItemsLoading,
  setIsNotValidTasksLoading,
  setNextMilestone,
  validateCustomRequestedItems,
  validateCustomRequestedItemsSuccess,
  validateMilestone,
  validateMilestoneSuccess,
} from '@/domains/milestones/store/milestonesSlice';
import { DynamicIcon } from '@/components/ui/icons/DynamicIcon/DynamicIcon';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';

function* getMilestoneRelatedEntityListSaga({ payload }: PayloadAction<GetMilestoneRelatedEntityListPayload>) {
  try {
    const claimSetId = yield select(selectCurrentClaimSetId);
    const { data } = yield call(
      HttpService.get,
      `/examiner/milestone/claimSet/get?claimSetId=${claimSetId}&${QueryParamsService.constructSearchParamsFromRecord(payload)}`,
      undefined,
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    let normalizedData: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data, {
      section: EdsDefinitionSections.Milestones,
    });

    normalizedData = normalizedData.filter((entity) => {
      return !(entity.entityName === EdsEntityCodes.ClaimEntity && entity.virtualFields?.isDeleted);
    });

    EdsDebugService.logResponse(EdsEntityCodes.Milestone, {
      section: EdsDefinitionSections.Milestones,
      rawResponse: data,
      normalizedResponse: normalizedData,
    });

    yield put(getMilestoneRelatedEntityListSuccess(normalizedData));
  } catch (error) {
    console.error(error);
  }
}

function* getEntityMilestoneDetailsSaga({ payload }: PayloadAction<EdsEntityUniqueFields>) {
  try {
    const isClaimSetLevel = payload.entityName === RequestedDataEntityLevel['1'];
    const isClaimLevel = payload.entityName === RequestedDataEntityLevel['2'];

    const { data } = yield call(
      HttpService.get,
      `/v2/${payload.entityName}/${payload.id}?includeEntities=${isClaimSetLevel ? 'Insured.Party.Person,' : ''}${isClaimLevel ? 'Benefit,Policy,' : ''}TaskEntities,RequestedItems.RequestedDocument.DocumentType,RequestedItems.RequestedData.RequestedDataType`,
      undefined,
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse = yield call(EdsAdapterService.normalize, data, {
      section: EdsDefinitionSections.Milestones,
    });

    EdsDebugService.logResponse(`Milestone ${payload.entityName}` as EdsEntityCodes, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse
        ? EdsAdapterService.generateSlotRecord<RequestedItemSlots>(normalizedResponse)
        : {},
    });

    yield put(getEntityMilestoneDetailsSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
    getEntityMilestoneDetailsError(payload);
  }
}

function* getMilestoneWithCategoryOptionsSaga({ payload }: PayloadAction<GetAvailableMilestonesPayload>) {
  if (!payload.entityType || !payload.entityId) {
    return;
  }
  try {
    yield put(setGetMilestoneOptionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/examiner/milestone/get-available-milestones`,
      {
        entityType: payload.entityType,
        entityId: payload.entityId,
        currentMilestoneId: payload.currentMilestoneId,
        incidentTypeId: payload.incidentTypeId,
        productTypeId: payload.productTypeId,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    const milestoneDefinitions = yield call(EdsAdapterService.getEntityDefinition, EdsEntityCodes.Milestone);
    const entityDefinitions = yield call(EdsAdapterService.getEntityDefinition, payload.entityType);

    const milestoneWithCategoryOptions = data.milestoneAndCategoryIds.map((option) => {
      const categoryLabel =
        EdsAdapterService.extractLabelFromAllowedValues(milestoneDefinitions.fields?.categoryId, option.categoryId) ||
        'unknown';
      const milestoneLabel =
        EdsAdapterService.extractLabelFromAllowedValues(entityDefinitions.fields.milestoneId, option.milestoneId) ||
        'unknown';

      return { value: option.milestoneId, categoryLabel, milestoneLabel };
    });

    yield put(
      getMilestoneWithCategoryOptionsSuccess({
        entityType: payload.entityType,
        entityId: payload.entityId.toString(),
        options: milestoneWithCategoryOptions,
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setGetMilestoneOptionsLoading(false));
  }
}

function* setNextMilestoneSaga({ payload }: PayloadAction<SetNextMilestonesPayload>) {
  if (!payload.entityType || !payload.entityId) {
    return;
  }
  const saveNextMilestone = async (value) => {
    if (!payload.entityId || !value) {
      return;
    }
    const editedFields: EditableField[] = [
      {
        entityId: +payload.entityId,
        entityName: payload.entityType as any,
        entityField: EntityField.MilestoneId,
        oldValue: payload.currentMilestoneId?.toString(),
        value: value.toString(),
      },
    ];
    try {
      await HttpService.post(
        `v2/update`,
        { changes: editedFields },
        {
          baseURL: import.meta.env.VITE_API_URL_EDS,
        },
      );

      SnacksService.add({
        type: SnackType.Success,
        title: 'Updates saved successfully!',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        slots: {
          left: React.createElement(DynamicIcon, { code: 'SUCCESS_ICON' }),
        },
      });

      EventService.dispatch(new EditModeEvents.Save(editedFields));
    } catch (error) {
      SnacksService.add({
        type: SnackType.Error,
        title: 'Failed to save the update.',
        content: 'Please try again',
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
      console.error(error);
    }
  };

  try {
    yield put(setSystemLoadingStatus(true));

    const { data } = yield call(
      HttpService.get,
      `/examiner/milestone/get-next-milestone`,
      {
        entityType: payload.entityType,
        entityId: payload.entityId,
        currentMilestoneId: payload.currentMilestoneId,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );
    yield put(
      setIsNextMilestonePossible({
        entityType: payload.entityType,
        entityId: payload.entityId.toString(),
        value: !!(data.milestoneAndCategoryIds && data.milestoneAndCategoryIds.length),
      }),
    );

    if (!data.isPossibleToSwitch) {
      SnacksService.add({
        type: SnackType.Warning,
        title: 'Next Milestone Warning',
        content: data.message || 'Please use dropdown and select manually.',
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
      return;
    }

    yield call(validateMilestoneSaga, {
      type: validateMilestone.type,
      payload: {
        currentMilestoneId: payload.currentMilestoneId,
        entityId: payload.entityId,
        entityType: payload.entityType,
        nextMilestoneId: data.milestoneAndCategoryIds[0].milestoneId,
        onValid: () => saveNextMilestone(data.milestoneAndCategoryIds[0].milestoneId.toString()),
        onInvalid: () => payload?.onInvalid?.(data.milestoneAndCategoryIds[0].milestoneId, saveNextMilestone),
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* validateMilestoneSaga({ payload }: PayloadAction<ValidateMilestonePayload>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const { data } = yield call(
      HttpService.get,
      `/examiner/milestone/validate-milestone`,
      {
        entityType: payload.entityType,
        entityId: payload.entityId,
        currentMilestoneId: payload.currentMilestoneId,
        nextMilestoneId: payload.nextMilestoneId,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield put(validateMilestoneSuccess(data));

    if (data.isValid) {
      payload?.onValid();
    } else {
      yield call(handleInvalidMilestoneSaga, { validationResult: data, handleInvalid: payload.onInvalid });
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* validateRequestedItemsSaga({ payload }: PayloadAction<GetMilestoneRelatedEntityListPayload>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/examiner/milestone/validate-requested-items`,
      {
        entityType: payload.entityType,
        entityId: payload.entityId,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    const notValidCustomRequestedItems: NotValidItem[] = data.notValidRequestedItems.reduce(
      (acc, item) => (item.isCustomRule ? [...acc, { id: item.id, validationStyle: item.validationStyle }] : acc),
      [],
    );

    yield put(
      validateCustomRequestedItemsSuccess({
        entityType: payload.entityType,
        entityId: payload.entityId,
        notValidCustomRequestedItems,
      }),
    );
  } catch (error) {
    console.error(error);
  }
}

function* handleInvalidMilestoneSaga({
  validationResult,
  handleInvalid,
}: {
  validationResult: MilestoneValidationResult;
  handleInvalid: () => void | Promise<void>;
}) {
  if (!validationResult.resultedValidationStyle) {
    return;
  }
  yield put(getNotValidRequestedItems());
  yield put(getNotValidTasks());
  handleInvalid();
}

function* getNotValidRequestedItemsSaga() {
  const notValidRequestedItems = yield select(selectNotValidRequestedItems);
  const notValidRequestedItemsIds = notValidRequestedItems.map((item) => item.id);

  if (!notValidRequestedItemsIds.length) {
    return;
  }
  yield put(setIsNotValidRequestedItemsLoading(true));

  try {
    const requestUrl = `/filter/${EdsEntityCodes.RequestedItem}?includes=RequestedDocument.DocumentType,RequestedData.RequestedDataType`;

    const { data } = yield call(
      HttpService.post,
      requestUrl,
      [
        {
          propertyName: edsToEntityFormat('id'),
          values: notValidRequestedItemsIds,
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse = yield call(EdsAdapterService.normalize, data.listings);

    yield put(getNotValidRequestedItemsSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setIsNotValidRequestedItemsLoading(false));
  }
}

function* getNotValidTasksSaga() {
  const notValidTasks = yield select(selectNotValidTasks);
  const notValidTasksIds = notValidTasks.map((item) => item.id);

  if (!notValidTasksIds.length) {
    return;
  }
  yield put(setIsNotValidTasksLoading(true));

  try {
    const requestUrl = `/filter/${EdsEntityCodes.Tasks}`;

    const { data } = yield call(
      HttpService.post,
      requestUrl,
      [
        {
          propertyName: edsToEntityFormat('id'),
          values: notValidTasksIds,
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse = yield call(EdsAdapterService.normalize, data.listings);

    yield put(getNotValidTasksSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setIsNotValidTasksLoading(false));
  }
}

function* getAllAvailableMilestonesSaga() {
  const isAlreadyLoading = yield select(selectIsAllAvailableMilestonesLoading);
  if (isAlreadyLoading) {
    return;
  }

  setIsAvaliableMilestonesLoading(true);
  try {
    const requestUrl = `/filter/${EdsEntityCodes.Milestone}`;

    const { data } = yield call(HttpService.post, requestUrl, [], {
      params: {
        perPage: 10000,
      },
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const [milestoneDefinitions, claimSetDefinitions, claimDefinitions, claimantDefinitions] = yield call(async () => {
      return Promise.all(
        [EdsEntityCodes.Milestone, EdsEntityCodes.ClaimSet, EdsEntityCodes.ClaimEntity, EdsEntityCodes.Claimant].map(
          (entityType) => EdsAdapterService.getEntityDefinition(entityType),
        ),
      );
    });

    const getDefinitionsForEntity = (entityDefinitions) =>
      data.listings.map((option) => {
        const categoryLabel =
          EdsAdapterService.extractLabelFromAllowedValues(milestoneDefinitions.fields?.categoryId, option.categoryId) ||
          'unknown';
        const milestoneLabel =
          EdsAdapterService.extractLabelFromAllowedValues(entityDefinitions.fields?.milestoneId, option.id) ||
          'unknown';

        return { value: option.id, milestoneLabel, categoryLabel };
      });

    yield put(
      getAllAvailableMilestonesSuccess({
        [EdsEntityCodes.ClaimSet]: getDefinitionsForEntity(claimSetDefinitions),
        [EdsEntityCodes.ClaimEntity]: getDefinitionsForEntity(claimDefinitions),
        [EdsEntityCodes.Claimant]: getDefinitionsForEntity(claimantDefinitions),
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    setIsAvaliableMilestonesLoading(false);
  }
}

function* saveNewMilestoneStateSaga({ payload }: PayloadAction<ValidateMilestonePayload>) {
  try {
    const requestUrl = `examiner/milestone/prepare-new-milestone-state`;

    yield call(
      HttpService.post,
      requestUrl,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
        params: {
          entityId: payload.entityId,
          entityType: payload.entityType,
          currentMilestoneId: payload.currentMilestoneId,
          nextMilestoneId: payload.nextMilestoneId,
        },
      },
    );

    switch (payload.entityType) {
      case EdsEntityCodes.ClaimSet:
        if (payload.entityId) {
          yield put(getClaimSetV2(payload.entityId.toString()));
        }
        break;
      case EdsEntityCodes.ClaimEntity:
        if (payload.claimSetId) {
          yield put(getClaimSetClaimListData({ claimSetId: payload.claimSetId.toString() }));
        }
        break;
      case EdsEntityCodes.Claimant:
        if (payload.claimId) {
          yield put(
            getClaimantsList({
              filters: [{ propertyName: edsToEntityFormat('claimId'), values: [payload.claimId] }],
            }),
          );
        }
        break;
      default:
        break;
    }
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the updates. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  }
}

export function* milestonesWatcher() {
  yield takeLatest([getMilestoneRelatedEntityList.type], getMilestoneRelatedEntityListSaga);
  yield takeEvery([getEntityMilestoneDetails.type], getEntityMilestoneDetailsSaga);
  yield takeLatest([getMilestoneWithCategoryOptions.type], getMilestoneWithCategoryOptionsSaga);
  yield takeLatest([validateMilestone.type], validateMilestoneSaga);
  yield takeLatest([getNotValidRequestedItems.type], getNotValidRequestedItemsSaga);
  yield takeLatest([getNotValidTasks.type], getNotValidTasksSaga);
  yield takeEvery([saveNewMilestoneState.type], saveNewMilestoneStateSaga);
  yield takeLatest([getAllAvailableMilestones.type], getAllAvailableMilestonesSaga);
  yield takeLatest([setNextMilestone.type], setNextMilestoneSaga);
  yield takeLatest([validateCustomRequestedItems.type], validateRequestedItemsSaga);
}
